/**
 * Chat Flow API — wraps /api/chat-flows endpoints.
 *
 * Used by the Unified Agent canvas (WhatsApp / Instagram / Messenger flows).
 * Mirrors `flowsApi` from campaignApi.ts in style and naming.
 *
 * Backend route: server/src/routes/chatFlows.ts
 * Backend model: prisma ChatFlow
 */
import type { ItemResponse, ListResponse, DeleteResponse } from '../../types/api.js';
import { apiRequest } from './core.js';

// ---- Types -----------------------------------------------------------------

/**
 * Persisted chat-flow node — matches the JSON shape stored in
 * `ChatFlow.nodes` on the server. Components read/write through the
 * dbToCanvas/canvasToDb transforms in AgentCanvas.tsx.
 */
export interface ChatFlowNode {
  id: string;
  type: string;
  position?: { x: number; y: number };
  data?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface ChatFlowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
  label?: string;
  [key: string]: unknown;
}

export type ChatFlowStatus = 'draft' | 'active' | 'paused' | 'archived';
export type ChatFlowType = 'DEFAULT' | 'CUSTOM';

export interface ChatFlow {
  id: string;
  workspaceId: string;
  name: string;
  description?: string;
  type: ChatFlowType;
  trigger?: string;
  triggerConfig?: Record<string, unknown>;
  audienceFilter?: Record<string, unknown>;
  nodes: ChatFlowNode[];
  edges: ChatFlowEdge[];
  viewport?: Record<string, unknown>;
  status: ChatFlowStatus;
  stats?: Record<string, unknown>;
  executionCount: number;
  lastExecutedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ChatFlowAnalytics {
  totalExecutions: number;
  completionRate: number;
  conversions: number;
  conversionRate: number;
}

export interface ChatFlowCreatePayload {
  name: string;
  description?: string;
  trigger?: string;
  triggerConfig?: Record<string, unknown>;
  nodes?: ChatFlowNode[];
  edges?: ChatFlowEdge[];
}

export type ChatFlowUpdatePayload = Partial<{
  name: string;
  description: string;
  trigger: string;
  triggerConfig: Record<string, unknown>;
  audienceFilter: Record<string, unknown>;
  nodes: ChatFlowNode[];
  edges: ChatFlowEdge[];
  viewport: Record<string, unknown>;
}>;

// ---- API client ------------------------------------------------------------

export const chatFlowsApi = {
  list: () => apiRequest<ListResponse<ChatFlow>>('/chat-flows'),

  get: (id: string) => apiRequest<ItemResponse<ChatFlow>>(`/chat-flows/${id}`),

  /**
   * Server creates a baseline DEFAULT flow if none exists for the workspace.
   * Useful for empty-state bootstrap on the My Flows screen.
   */
  getDefault: () => apiRequest<ItemResponse<ChatFlow>>('/chat-flows/default'),

  /**
   * Delete and regenerate the Default Flow from the current factory graph.
   * A workspace seeded before the AI-fallback branch existed keeps the old
   * "free text → hand off" graph forever otherwise.
   */
  resetDefault: () =>
    apiRequest<ItemResponse<ChatFlow>>('/chat-flows/default/reset', { method: 'POST' }),

  create: (data: ChatFlowCreatePayload) =>
    apiRequest<ItemResponse<ChatFlow>>('/chat-flows', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: ChatFlowUpdatePayload) =>
    apiRequest<ItemResponse<ChatFlow>>(`/chat-flows/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  /**
   * Runs server-side validation (must have trigger + at least one
   * message/ai_response node) and flips status to 'active'.
   */
  activate: (id: string) =>
    apiRequest<ItemResponse<ChatFlow>>(`/chat-flows/${id}/activate`, { method: 'POST' }),

  pause: (id: string) =>
    apiRequest<ItemResponse<ChatFlow>>(`/chat-flows/${id}/pause`, { method: 'POST' }),

  duplicate: (id: string) =>
    apiRequest<ItemResponse<ChatFlow>>(`/chat-flows/${id}/duplicate`, { method: 'POST' }),

  delete: (id: string) =>
    apiRequest<DeleteResponse>(`/chat-flows/${id}`, { method: 'DELETE' }),

  getAnalytics: (id: string) =>
    apiRequest<ItemResponse<ChatFlowAnalytics>>(`/chat-flows/${id}/analytics`),
};
